import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";

/**
 * Renders a destructive-confirm dialog that requires the user to type
 * a specific phrase (usually the record's name) before the action fires.
 *
 * Usage:
 *   const [confirming, setConfirming] = useState(null);
 *   ...
 *   <button onClick={() => setConfirming({ item, phrase: item.name, onConfirm: async () => { ... } })}>Delete</button>
 *   <ConfirmTypeDialog
 *     open={!!confirming}
 *     onOpenChange={o => !o && setConfirming(null)}
 *     phrase={confirming?.phrase}
 *     title="Deactivate workspace?"
 *     description="This will block sign-in for all users of this tenant."
 *     confirmLabel="Deactivate"
 *     onConfirm={confirming?.onConfirm}
 *   />
 */
export default function ConfirmTypeDialog({
  open,
  onOpenChange,
  phrase = "",
  title = "Confirm destructive action",
  description = "This action is destructive and cannot be undone.",
  confirmLabel = "Delete",
  onConfirm,
  testidPrefix = "confirm-type",
}) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = input.trim() === (phrase || "").trim() && !!phrase;

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    try { await onConfirm?.(); onOpenChange?.(false); setInput(""); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange?.(o); if (!o) setInput(""); }}>
      <DialogContent className="rounded-md max-w-md" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-[#DC2626]">
            <AlertTriangle className="w-5 h-5" /> {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-600">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded text-xs text-[#7F1D1D]">
            To continue, type <span className="font-mono font-bold bg-white px-1.5 py-0.5 border border-[#FCA5A5]">{phrase}</span> exactly below.
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Confirmation phrase</Label>
            <Input
              data-testid={`${testidPrefix}-input`}
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={phrase}
              className="mt-1 font-mono"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid={`${testidPrefix}-cancel`}>Cancel</Button>
          <Button
            data-testid={`${testidPrefix}-submit`}
            onClick={submit}
            disabled={!matches || busy}
            className="bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
